import React, { useState, useEffect } from 'react';
import { Info, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const BACKEND_URL = '';

/**
 * Page Explainer info icon. Drop into any page header:
 *   <PageExplainer pageKey="forum" />
 *
 * The icon is invisible until an admin has authored content for that page key
 * — keeps the UI clean. Click opens a modal that members can dismiss.
 */
export default function PageExplainer({ pageKey, label }) {
  const [explainer, setExplainer] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!pageKey) return;
    fetch(`${BACKEND_URL}/api/page-explainers/${pageKey}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.exists && d.content) setExplainer(d); })
      .catch(() => {});
  }, [pageKey]);

  if (!explainer) return null;

  const title = explainer.title || label || 'About this page';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Page explainer"
        title="Page explainer"
        data-testid={`page-explainer-trigger-${pageKey}`}
        className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-brand-navy/5 hover:bg-brand-navy/15 text-brand-navy transition-colors"
      >
        <Info className="h-4 w-4" />
        <span className="sr-only">Page explainer</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`page-explainer-modal-${pageKey}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-brand-navy">
              <Info className="h-4 w-4" /> {title}
            </DialogTitle>
          </DialogHeader>
          <div
            className="prose prose-sm prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap"
            data-testid={`page-explainer-content-${pageKey}`}
          >
            {explainer.content}
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setOpen(false)} variant="outline"
              data-testid={`page-explainer-close-${pageKey}`}>
              <X className="h-3.5 w-3.5 mr-1.5" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
